import { useState, useEffect, useContext } from 'react';
import * as wasm from 'sched';
import { WorkspaceContext } from '../context/WorkspaceContext';

// Hook to access pending changes
export function usePendingChanges() {
  const { safeData } = useContext(WorkspaceContext);
  const [pendingChanges, setPendingChanges] = useState<wasm.PendingChange[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!safeData) return;

    // Create a callback function to receive pending changes updates
    const callback = (newPendingChanges: wasm.PendingChange[]) => {
      console.log('Pending changes updated:', newPendingChanges);
      setPendingChanges(newPendingChanges);
    };

    // Convert callback to a JS function that Rust can call
    const jsCallback = callback as unknown as Function;

    // Subscribe to pending changes - this is an async function
    const subscribeToPendingChanges = async () => {
      try {
        setLoading(true);

        // Subscribe to pending changes
        await wasm.DataManager.select_pending_changes(safeData, jsCallback);

        setLoading(false);
      } catch (err) {
        // Initialize with empty array to avoid errors
        setPendingChanges([]);
        setLoading(false);
      }
    };

    subscribeToPendingChanges();

    // Fetch initial pending changes
    const fetchInitialPendingChanges = async () => {
      try {
        const changes = await wasm.DataManager.list_pending_changes(safeData);
        setPendingChanges(changes);
      } catch (err) {
        console.error('Error fetching initial pending changes:', err);
      }
    };

    fetchInitialPendingChanges();

    // Cleanup function to unsubscribe
    return () => {
      if (safeData && (safeData as any).__wbg_ptr !== 0) {
        // This is also async, but we can't await in cleanup function
        // Just fire and forget, but log any errors
        wasm.DataManager.unsubscribe(safeData, jsCallback)
          .catch((err: any) => console.error('Error unsubscribing from pending changes:', err));
      }
    };
  }, [safeData]);

  // Function to save pending changes to the server
  const savePendingChanges = async (apiClient: wasm.WrappedApiClient, workspaceId: string, token: string) => {
    if (!safeData || pendingChanges.length === 0) return;

    try {
      await wasm.DataManager.save(safeData, apiClient, workspaceId, token);
      return true;
    } catch (err) {
      console.error('Error saving pending changes:', err);
      throw err;
    }
  };

  // Function to undo a specific pending change by index
  const undoPendingChange = async (index: number) => {
    if (!safeData) return;

    try {
      await wasm.DataManager.undo_pending_change(safeData, index);
      return true;
    } catch (err) {
      console.error('Error undoing pending change:', err);
      throw err;
    }
  };

  // Function to undo all pending changes
  const undoAllPendingChanges = async () => {
    if (!safeData) return;

    try {
      await wasm.DataManager.undo_pending_changes(safeData);
      return true;
    } catch (err) {
      console.error('Error undoing all pending changes:', err);
      throw err;
    }
  };

  return {
    pendingChanges,
    loading,
    savePendingChanges,
    undoPendingChange,
    undoAllPendingChanges
  };
}
